import { useMemo } from 'react';
import {
  CellContext,
  ColumnDef,
  getCoreRowModel,
  HeaderContext,
  SortingState,
  TableOptions,
  useReactTable,
} from '@tanstack/react-table';
import { IndeterminateCheckbox } from './IndeterminateCheckbox';

type UseTableProps<T> = {
  data?: T[] | null;
  columns: ColumnDef<T>[];
  columnVisibility?: Record<string, boolean>;
  isLoading: boolean;
  totalItems?: number;
  rowSelection?: Record<string, boolean>;
  setRowSelection?: (rowSelection: Record<string, boolean>) => void;
  pagination?: {
    pageIndex: number;
    pageSize: number;
  };
  setPagination?: (pagination: { pageIndex: number; pageSize: number }) => void;
  sorting?: SortingState;
  setSorting?: (sorting: SortingState) => void;
  tableOptions?: Omit<
    TableOptions<T>,
    'data' | 'columns' | 'getCoreRowModel'
  >;
};

export const useTable = <T,>({
  data,
  isLoading,
  columns,
  columnVisibility,
  totalItems,
  rowSelection,
  setRowSelection,
  pagination,
  setPagination,
  sorting,
  setSorting,
  tableOptions,
}: UseTableProps<T>) => {
  const tableData = useMemo(
    () =>
      isLoading
        ? Array(pagination?.pageSize || 8).fill({})
        : data || [],
    [isLoading, pagination?.pageSize, data],
  );

  const usingRowSelection = rowSelection != null && setRowSelection != null;

  const extendedColumns = useMemo(() => {
    return [
      ...(usingRowSelection
        ? [
            {
              id: 'select',
              size: 0,
              header: ({ table }: HeaderContext<T, unknown>) => (
                <IndeterminateCheckbox
                  checked={table.getIsAllRowsSelected()}
                  indeterminate={table.getIsSomeRowsSelected()}
                  onChange={table.getToggleAllRowsSelectedHandler()}
                />
              ),
              cell: ({ row }: CellContext<T, unknown>) =>
                row.getCanSelect() ? (
                  <IndeterminateCheckbox
                    checked={row.getIsSelected()}
                    indeterminate={row.getIsSomeSelected()}
                    onChange={row.getToggleSelectedHandler()}
                  />
                ) : null,
            },
          ]
        : []),
      ...columns,
    ];
  }, [columns, usingRowSelection]);

  return useReactTable({
    data: tableData,
    columns: extendedColumns,
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange: setPagination,
    manualPagination: true,
    rowCount: totalItems,
    manualSorting: true,
    onSortingChange: setSorting,
    ...(usingRowSelection ? { onRowSelectionChange: setRowSelection } : {}),
    state: {
      sorting,
      pagination,
      rowSelection,
      columnVisibility,
    },
    ...(tableOptions ? tableOptions : {}),
  });
};
